import I18n from "common/i18n";
import { useScale, usePlaySound } from "common/utils";
import { IconProgress, IconUserProfile, IconItem, IconMail } from "features/user/assets";
import { UserPageTab } from "./UserPageTab";

const TAB_BAR_HEIGHT = 142;
const TAB_BAR_BORDER = 6;
const TAB_ITEM_WIDTH = 160;

type TabConfig = {
  tab: UserPageTab;
  labelKey: string;
  renderIcon: (size: number) => React.ReactNode;
};

const TAB_CONFIGS: TabConfig[] = [
  {
    tab: UserPageTab.PROFILE,
    labelKey: "MSG_USER_PROFILE_INFO",
    renderIcon: (size) => <IconUserProfile viewBox="0 0 48 48" width={size} height={size} />,
  },
  {
    tab: UserPageTab.MESSAGE,
    labelKey: "MSG_USER_PROFILE_MESSAGE",
    renderIcon: (size) => <IconMail viewBox="0 0 48 48" width={size} height={size} />,
  },
  {
    tab: UserPageTab.PROGRESS,
    labelKey: "MSG_USER_TAB_PROGRESS",
    renderIcon: (size) => <IconProgress viewBox="0 0 48 48" width={size} height={size} />,
  },
  {
    tab: UserPageTab.STOCK,
    labelKey: "MSG_USER_TAB_STOCK",
    renderIcon: (size) => <IconItem viewBox="0 0 48 48" width={size} height={size} />,
  },
  {
    tab: UserPageTab.PLAN,
    labelKey: "MSG_USER_TAB_PLAN",
    renderIcon: () => (
      <span className="material-symbols-outlined material-symbols-filled type-hero !leading-none text-orange-100">
        workspace_premium
      </span>
    ),
  },
];

const TAB_ICON_SIZE = 48;

type UserBottomTabBarProps = {
  activeTab: UserPageTab;
  hasUnreadMessage: boolean;
  showPlanTab: boolean;
  onTabChange: (tab: UserPageTab) => void;
};

export const UserBottomTabBar = ({
  activeTab,
  hasUnreadMessage,
  showPlanTab,
  onTabChange,
}: UserBottomTabBarProps) => {
  const { scale } = useScale();
  const play = usePlaySound();
  const visibleTabs = TAB_CONFIGS.filter(({ tab }) => showPlanTab || tab !== UserPageTab.PLAN);

  const handleClick = (tab: UserPageTab) => () => {
    play();
    onTabChange(tab);
  };

  return (
    <div
      className="flex-col-el items-center justify-end shrink-0 w-full pointer-events-none"
      style={{
        marginBottom: 16 * scale,
        height: TAB_BAR_HEIGHT * scale,
      }}
    >
      <div
        className="flex-row-center pointer-events-auto origin-bottom overflow-hidden rounded-2xl border-[6px] border-solid border-white bg-white shadow-[0_-1px_0_rgba(237,227,207,0.9),0_4px_18px_rgba(63,74,97,0.12)]"
        style={{
          transform: `scale(${scale})`,
          height: TAB_BAR_HEIGHT,
        }}
      >
        {visibleTabs.map(({ tab, labelKey, renderIcon }) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              className="type-inherit-font flex-col-el flex-center cursor-pointer relative bg-transparent p-0 m-0 border-0 outline-none appearance-none focus:outline-none active:translate-y-[2px] transition-transform"
              style={{
                width: TAB_ITEM_WIDTH,
                height: TAB_BAR_HEIGHT - TAB_BAR_BORDER * 2,
              }}
              onClick={handleClick(tab)}
            >
              <div
                className={`flex-col-el flex-center gap-1 absolute inset-[6px] px-2 py-2 rounded-2xl border-2 border-solid transition-colors ${
                  isActive
                    ? "border-green-80 bg-green-60 text-textcolor-black"
                    : "border-transparent bg-transparent text-textcolor-black/60"
                }`}
              >
                <div className="flex-col-el flex-center w-[48px] h-[48px] shrink-0">
                  {renderIcon(TAB_ICON_SIZE)}
                </div>
                <p className="type-text type-body leading-tight whitespace-pre text-center">
                  {I18n.t(labelKey)}
                </p>
              </div>

              {tab === UserPageTab.MESSAGE && hasUnreadMessage && (
                <div className="flex-col-el flex-center absolute top-[8px] right-[8px] bg-danger rounded-full w-[32px] h-[32px] z-20 shadow-[0_1px_3px_rgba(0,0,0,0.2)]" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
